import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { Store } from '../../stateManagement';
import { DatesHelper } from '../../common';
import { DashboardThumbnailWidget } from '../../widgets';
import {
    CharmResult,
    ConnectionPointType,
    DashboardPeriod,
    Group,
    GroupConfigMode,
    ICharm,
    ICharmFactoryItem,
    IStringDateRange2,
    ITagsStatusCount,
    ITask,
    ITasksStatus,
    ResultsStatus,
    ServiceStatus,
    TaskStatus,
    TicketCounts
} from '../../models';
import {
    IAuthorizationService,
    ICurrentApplicationService,
    IDashboardLoadService,
    IDashboardService,
    IDataFolderService,
    IManualInputsService,
    IModelIntegrityService,
    IOnlineComputationService,
    ITagDefinitionMapper,
    ITicketService,
    IValidationPageConfigurationService,
    IstateAuthorizationService
} from '../../services';
import { mapConnectionPointCharm } from './mapConnectionPointCharm';
import { infoTextColor } from '../../common/constants';

export interface MapHomeStoreToThis {
    dashboards: DashboardThumbnailWidget[];
    tiles: ICharm[];
}

export interface IHomeService {
    refreshQualityWidgetValues(dashboards: DashboardThumbnailWidget[]): angular.IPromise<void[]>;
    refreshCharms(tiles: ICharm[], store: Store): MapHomeStoreToThis;
    refreshDataInputCharms(store: Store): angular.IPromise<any>;
}

export class HomeService implements IHomeService {
    static serviceId = 'homeService';

    private _factory: ICharmFactoryItem[];

    static $inject = [
        '$q',
        '$state',
        'stateAuthorizationService',
        'authorizationService',
        'manualInputsService',
        'onlineComputationService',
        'dataFolderService',
        'tagDefinitionMapper',
        'currentApplicationService',
        'ticketService',
        'dashboardLoadService',
        'dashboardService',
        'modelIntegrityService',
        'validationPageConfigurationService',

    ];

    constructor(
        private _$q: angular.IQService,
        private _$state: angular.ui.IStateService,
        private _stateAuthorizationService: IstateAuthorizationService,
        private _authorizationService: IAuthorizationService,
        private _manualInputService: IManualInputsService,
        private _onlineComputationService: IOnlineComputationService,
        private _dataFolderService: IDataFolderService,
        private _tagDefinitionMapper: ITagDefinitionMapper,
        private _currentApplicationService: ICurrentApplicationService,
        private _ticketService: ITicketService,
        private _dashboardLoadService: IDashboardLoadService,
        private _dashboardService: IDashboardService,
        private _modelIntegrityService: IModelIntegrityService,
        private _validationPageConfigurationService: IValidationPageConfigurationService,
    ) {
        this._factory = this.createFactory();
    }

    refreshQualityWidgetValues(dashboards: DashboardThumbnailWidget[]): angular.IPromise<void[]> {
        const promises = dashboards
            .filter(d => d.defaultPeriod !== DashboardPeriod.None
                && d.configuration.widgets
                && d.configuration.widgets.filter(w => !!w.configuration && w.configuration.qualityWidgetType !== undefined))
            .map(d => {
                const widgets = d.configuration.widgets.filter(w => !!w.configuration && w.configuration.qualityWidgetType !== undefined);
                if (widgets.length > 0) {
                    return this._dashboardLoadService
                        .getRange(d.defaultPeriod)
                        .then(r => this._dashboardLoadService.refresh(widgets, r.beginDateTime, r.endDateTime))
                        .then(data => this._dashboardLoadService.assignValues(data, widgets));
                }
            });
        return this._$q.all(promises);
    }

    refreshDataInputCharms(store: Store): angular.IPromise<any> {
        let groupsWithWidgets: Group[];
        let filteredGroups: Group[];
        const validationPages = Object.values(store.validationPageConfigurations.dictionary);
        return this._validationPageConfigurationService.getGroupsWithWidgets().then(d => {
            groupsWithWidgets = d;
            filteredGroups = _.values(store.groups.dictionary).filter(d => (d.isHomePage));
            filteredGroups = filteredGroups.filter(d =>
                (!!d.type && d.configMode == GroupConfigMode.ByType && validationPages.find(x => x.name === d.type && x.validationWidgets.length > 0))
                ||
                ((d.configMode == GroupConfigMode.ByGroup) && (groupsWithWidgets).find(x => x.guid === d.guid))
            );
            return filteredGroups.map(g => {
                return {
                    icon: 'fa-keyboard-o',
                    color: '#939',
                    go: () => this._$state.go('validation', { groupGuid: g.guid }),
                    text: g.uniqueName,
                    isLoading: false,
                    stateName: g.guid,
                    id: g.guid,
                    isPopulated: true,
                    populate: (charm: ICharm) => this.populateTicketsCharm(charm),
                    isVisible: true
                };
            });
        });
    }

    refreshCharms(tiles: ICharm[], store: Store): MapHomeStoreToThis {
        if (!tiles || (tiles && !tiles.length)) {
            tiles = this.createCharms(store);
        }
        tiles.filter(tile => !tile.isPopulated)
            .forEach(tile => {
                if (tile.populate) {
                    tile = tile.populate(tile, store);
                }
            });
        let thumbs: DashboardThumbnailWidget[] = [];
        if (store.dashboards.isLoaded) {
            const dashboards = _.values(store.dashboards.dictionary)
                .filter(d => d.isHomePage)
                .sort((a, b) => a.sortOrder > b.sortOrder ? 1 : -1);
            thumbs = this._dashboardService.getDashboardThumbnails(dashboards);
        }
        return {
            dashboards: thumbs,
            tiles: tiles,
        };
    }

    private createFactory(): ICharmFactoryItem[] {
        return [
            {
                state: 'models.integrity',
                create: () => this.createIntegrityCharm()
            },
            {
                state: 'tags',
                useEditModeRoles: true,
                create: () => this.createTagsCharm()
            },
            {
                state: 'jobsDefinition',
                create: () => this.createTasksCharm()
            },
            {
                state: 'jobsDefinition',
                create: () => this.createServiceStatusCharm()
            },
            {
                state: 'moreTools.connectionPoints',
                create: () => this.createConnectionPointsCharm()
            },
            {
                state: 'logs',
                create: () => this.createLogsCharm()
            },
            {
                state: 'ticketing.kanban',
                create: () => this.createTicketsCharm()
            },
            {
                state: 'manualInputs',
                create: () => this.createManualInputsCharm()
            },
            {
                state: 'bulkInsert',
                create: () => this.createBulkInsertCharm()
            },
            {
                state: 'dataFolder',
                create: () => this.createDataFolderCharm()
            },
            {
                state: 'approval',
                create: () => this.createApprovalCharm()
            }
        ];
    }

    private createCharms(store: Store): ICharm[] {
        let tiles: ICharm[] = [];
        if (store.layout.isLoaded) {
            this._factory.forEach(f => {
                const state = this._$state.get(f.state);

                // Handle case when charm display doesn't depend on state roles
                // Usual case, when charm display match state roles
                const hasAccess = f.useEditModeRoles
                    ? this._authorizationService.isInRoles(state.data.editRoles)
                    : this._stateAuthorizationService.canAccess(f.state);
                const isMobileMode = store.layout.value.isMobileMode;
                if (hasAccess && (!isMobileMode || !!state.data.isMobileVisible)) {
                    tiles.push(f.create(store));
                }
            });
            tiles = tiles.filter(charm => !!charm);
        }
        return tiles;
    }

    private getEmptyCharmResultAsync(): angular.IPromise<CharmResult> {
        return this._$q.when({
            isWarning: false,
            isDanger: false
        });
    }

    private createIntegrityCharm(): ICharm {
        return {
            icon: 'fa-sitemap',
            color: '#990',
            go: charm => {
                const state = charm.values['count'] > 0
                    ? 'models.integrity'
                    : 'models.structure';
                this._$state.go(state);
            },
            text: 'Model integrity',
            isLoading: true,
            stateName: 'models.integrity',
            callback: () => this.getEmptyCharmResultAsync(),
            id: 'model',
            isPopulated: false,
            populate: (charm: ICharm) => this.populateIntegrityCharm(charm),
            isVisible: true,
            values: { count: 0 }
        };
    }

    private populateIntegrityCharm(charm: ICharm): ICharm {
        this._modelIntegrityService
            .getAll()
            .then(issues => {
                const hasIssues = (issues.length > 0);

                charm.text = hasIssues
                    ? '_count_ model issue(s)'
                    : 'Model verified';
                charm.tooltip = charm.text;
                charm.values = { count: issues.length };
                charm.callback = () => this._$q.when({ isWarning: false, isDanger: hasIssues });
                charm.isLoading = false;
                charm.isPopulated = true;

                return charm;
            });

        return charm;
    }

    private createTagsCharm(): ICharm {
        return {
            icon: 'fa-tags',
            color: '#990',
            go: () => this._$state.go('tags', { searchString: this.getTagsSearchString() }),
            text: 'Tags',
            isLoading: true,
            stateName: 'tags',
            callback: () => this.getEmptyCharmResultAsync(),
            id: 'tags',
            isPopulated: false,
            populate: (charm: ICharm, store: Store) => this.populateTagsCharm(charm, store),
            isVisible: true,
            values: {}
        };
    }

    private populateTagsCharm(charm: ICharm, store: Store): ICharm {
        if (store.tags.isLoaded
            && store.connectionPoints.isLoaded
            && store.variablesTypes.isLoaded
            && store.variableSubTypes.isLoaded
            && store.layout.isLoaded
            && !charm.isPopulated
        ) {
            charm.isPopulated = true;
            const { tagsDto, connectionPoints, variablesTypes, variableSubTypes } = store;
            const tagDtoList = _.values(tagsDto.dictionary);
            const connectionPointList = _.values(connectionPoints.dictionary);
            const tagVmList = this._tagDefinitionMapper.mapTagsVm(tagDtoList, connectionPointList, variablesTypes.dictionary, variableSubTypes.dictionary);
            const tagsStatus: ITagsStatusCount = tagVmList && tagVmList.length
                ? this._tagDefinitionMapper.checkTagsStatus(tagVmList, variablesTypes.dictionary)
                : {
                    invalid: 0,
                    requireReview: 0,
                    ignored: 0,
                    total: 0
                };
            charm.go = () => this._$state.go('tags', { searchString: this.getTagsSearchString(tagsStatus) });
            charm.callback = () => this.getTagsStatus(tagsStatus);
            charm.isLoading = false;
        }
        return charm;
    }

    /**
     * Get search string to use as state param for Tags page search bar
     * @param tagsStatus All tags status
     */
    private getTagsSearchString(tagsStatus?: ITagsStatusCount): string {
        if (tagsStatus.invalid > 0) {
            return 's:err';
        } else if (tagsStatus.requireReview > 0) {
            return 's:warning';
        } else {
            return '';
        }
    }

    private getTagsStatus(tagsStatus: ITagsStatusCount): angular.IPromise<CharmResult> {
        return this._$q.when({
            values: this.getTagsValues(tagsStatus),
            text: this.getTagsMessage(tagsStatus),
            isDanger: tagsStatus.invalid > 0,
            isWarning: tagsStatus.invalid === 0 && tagsStatus.requireReview > 0
        });
    }

    private getTagsMessage(tagsStatus: ITagsStatusCount): string {
        let message: string;

        if (tagsStatus.invalid > 0) {
            message = '_invalid_ incorrectly configured tag(s)';
        } else if (tagsStatus.requireReview > 0) {
            message = '_requirereview_ tag(s) requiring review';
        } else if (tagsStatus.ignored > 0) {
            message = '_ignored_ ignored tag(s)';
        } else {
            message = '_total_ tag(s)';
        }

        return message;
    }

    private getTagsValues(tagsStatus: ITagsStatusCount): ITagsStatusCount {
        let values: ITagsStatusCount = { invalid: 0, requireReview: 0, ignored: 0, total: 0 };

        if (tagsStatus.invalid > 0) {
            values = { invalid: tagsStatus.invalid, requireReview: 0, ignored: 0, total: tagsStatus.total };
        } else if (tagsStatus.requireReview > 0) {
            values = { invalid: 0, requireReview: tagsStatus.requireReview, ignored: 0, total: tagsStatus.total };
        } else if (tagsStatus.ignored > 0) {
            values = { invalid: 0, requireReview: 0, ignored: tagsStatus.ignored, total: tagsStatus.total };
        } else {
            values = { invalid: 0, requireReview: 0, ignored: 0, total: tagsStatus.total };
        }

        return values;
    }

    private createTasksCharm(): ICharm {
        return {
            icon: 'fa-tasks',
            color: '#939',
            go: () => this._$state.go('jobsDefinition'),
            text: 'Tasks',
            isLoading: true,
            stateName: 'jobsDefinition',
            callback: () => this.getEmptyCharmResultAsync(),
            id: 'tasks',
            isPopulated: false,
            populate: (charm: ICharm, store: Store) => this.populateTasksCharm(charm, store),
            isVisible: true,
            values: {}
        };
    }

    private populateTasksCharm(charm: ICharm, store: Store): ICharm {
        // Remove next line when tasks.actionCreator will be fixed
        charm.isLoading = false;
        if (store.tasks.isLoaded && !charm.isPopulated) {
            charm.isPopulated = true;
            const { tasks } = store;
            const taskList = _.values(tasks.dictionary);
            charm.callback = () => this.getTasksStatus(taskList);
            charm.isLoading = false;
        }
        return charm;
    }

    /**
     * Get tasks status
     * @param taskList task list
     */
    private getTasksStatus(taskList: ITask[]): angular.IPromise<CharmResult> {
        const tasksStatus: ITasksStatus = {
            failed: 0,
            cancelled: 0,
            pending: 0,
            completed: 0,
            total: 0
        };

        taskList.forEach(task => {
            // Failed
            if (task.status === TaskStatus.Failed) {
                tasksStatus.failed++;
                // Cancelled
            } else if (task.status === TaskStatus.Cancelling || task.status === TaskStatus.Cancelled) {
                tasksStatus.cancelled++;
                // Pending and ongoing
            } else if (task.status === TaskStatus.Created || task.status === TaskStatus.Started) {
                tasksStatus.pending++;
                // Completed
            } else if (task.status === TaskStatus.Completed) {
                tasksStatus.completed++;
            } else {
                tasksStatus.total++;
            }
        });
        return this._$q.when({
            values: this.getTasksValues(tasksStatus),
            text: this.getTasksMessage(tasksStatus),
            isDanger: tasksStatus.failed > 0,
            isWarning: tasksStatus.cancelled > 0
        });
    }

    /**
     * Get message to display
     * @param tasksStatus All tasks status
     */
    private getTasksMessage(tasksStatus: ITasksStatus): string {
        let message: string;

        if (tasksStatus.failed > 0) {
            message = '_failed_ failed task(s)';
        } else if (tasksStatus.cancelled > 0) {
            message = '_cancelled_ cancelled task(s)';
        } else if (tasksStatus.pending > 0) {
            message = '_pending_ pending task(s)';
        } else if (tasksStatus.completed > 0) {
            message = '_completed_ completed task(s)';
        } else {
            message = 'Task(s)';
        }

        return message;
    }

    /**
     * Get values to display
     * @param tasksStatus All tasks status
     */
    private getTasksValues(tasksStatus: ITasksStatus): ITasksStatus {
        let values: ITasksStatus = {} as ITasksStatus;

        if (tasksStatus.failed > 0) {
            values = { failed: tasksStatus.failed, cancelled: 0, pending: 0, completed: 0, total: 0 };
        } else if (tasksStatus.cancelled > 0) {
            values = { failed: 0, cancelled: tasksStatus.cancelled, pending: 0, completed: 0, total: 0 };
        } else if (tasksStatus.pending > 0) {
            values = { failed: 0, cancelled: 0, pending: tasksStatus.pending, completed: 0, total: 0 };
        } else if (tasksStatus.completed > 0) {
            values = { failed: 0, cancelled: 0, pending: 0, completed: tasksStatus.completed, total: 0 };
        } else {
            values = { failed: 0, cancelled: 0, pending: 0, completed: 0, total: 0 };
        }

        return values;
    }

    private createServiceStatusCharm(): ICharm {
        return {
            icon: 'fa-clock-o',
            color: '#939',
            go: () => this._$state.go('jobsDefinition'),
            text: 'Service status',
            isLoading: true,
            stateName: 'jobsDefinition',
            callback: () => this.getEmptyCharmResultAsync(),
            id: 'onlineJobs',
            isPopulated: false,
            populate: (charm: ICharm, store: Store) => this.populateServiceStatusCharm(charm, store),
            isVisible: true,
            values: {}
        };
    }

    private populateServiceStatusCharm(charm: ICharm, store: Store): ICharm {
        if (!charm.isPopulated) {
            charm.isPopulated = true;
            charm.callback = () => this.getServiceStatus(charm);
        }
        return charm;
    }

    private getServiceStatus(charm: ICharm): angular.IPromise<CharmResult> {
        return this._onlineComputationService
            .status()
            .then(status => {
                if (status === ServiceStatus.Offline) {
                    charm.isLoading = false;
                    return {
                        isWarning: false,
                        isDanger: true,
                        isFatal: true,
                        text: 'WiDE service is not running.',
                        values: {}
                    };
                } else {
                    return this.getSchedulingStatus(charm);
                }
            });
    }

    private getSchedulingStatus(charm: ICharm): angular.IPromise<CharmResult> {
        return this._onlineComputationService
            .getSchedulingViewModels()
            .then(data => {
                const inactive = data.list.filter(d => d.isInactive);
                const outOfDate = data.list.filter(d => d.isOutOfDate);
                const isWarning = inactive.length && !outOfDate.length;
                const isOutdated = data.isModelOutdated;
                const isDanger = outOfDate.length > 0;
                const message = isDanger
                    ? '_outOfDateCount_ job(s) need to be restarted.'
                    : isOutdated
                        ? 'Model has been changed. Restart scheduled tasks to take changes into account.'
                        : isWarning
                            ? '_inactiveJobsCount_ inactive job(s)'
                            : '_activeJobsCount_ active job(s)';
                const values = {
                    inactiveJobsCount: inactive.length,
                    activeJobsCount: data.list.filter(d => d.isScheduled && !d.isInactive).length,
                    outOfDateCount: outOfDate.length
                };
                const tooltip = isDanger ? 'Model has been changed. Restart scheduled tasks to take changes into account.' : null;
                charm.isLoading = false;
                return {
                    isWarning: isWarning || isOutdated,
                    isDanger,
                    text: message,
                    values,
                    tooltip
                };
            });
    }

    private createConnectionPointsCharm(): ICharm {
        return {
            icon: 'fa-connectdevelop',
            color: infoTextColor,
            go: () => this._$state.go('moreTools.connectionPoints'),
            text: 'Connection points',
            isLoading: true,
            stateName: 'moreTools.connectionPoints',
            callback: () => this.getEmptyCharmResultAsync(),
            id: 'connectionPoints',
            isPopulated: false,
            populate: (charm: ICharm, store: Store) => this.populateConnectionPointsCharm(charm, store),
            isVisible: true,
            values: {}
        };
    }

    private populateConnectionPointsCharm(charm: ICharm, store: Store): ICharm {
        if (store.connectionPoints.isLoaded && store.connectionPointsStatuses.isLoaded && !charm.isPopulated) {
            charm.isPopulated = true;
            const { connectionPoints, connectionPointsStatuses } = store;
            const { level, message, params } = mapConnectionPointCharm(connectionPoints, connectionPointsStatuses);
            charm.callback = () => this._$q.when({
                isDanger: level === ResultsStatus.Error,
                isWarning: level === ResultsStatus.Warning,
                text: message,
                values: params
            });
            charm.isLoading = false;
        }
        return charm;
    }

    private createLogsCharm(): ICharm {
        return {
            icon: 'fa-stethoscope',
            color: '#939',
            go: () => this._$state.go('logs'),
            text: 'Logs',
            isLoading: true,
            stateName: 'logs',
            id: 'logs',
            isPopulated: false,
            populate: (charm: ICharm) => this.populateLogsCharm(charm),
            isVisible: true,
            values: {}
        };
    }

    private populateLogsCharm(charm: ICharm): ICharm {
        if (!charm.isPopulated) {
            charm.isPopulated = true;
            charm.isLoading = false;
        }
        return charm;
    }

    // we decided not to use store
    private createTicketsCharm(): ICharm {
        const linkTarget = 'ticketing.kanban';
        const result: ICharm = {
            icon: 'fa-ticket',
            color: '#990',
            go: () => this._$state.go(linkTarget),
            text: 'Tickets',
            isLoading: true,
            stateName: 'ticketing',
            callback: () => this.getTicketCharmResults(result),
            id: 'tickets',
            isPopulated: false,
            populate: (charm: ICharm) => this.populateTicketsCharm(charm),
            isVisible: true
        };
        return result;
    }

    private populateTicketsCharm(charm: ICharm): ICharm {
        if (!charm.isPopulated) {
            charm.isPopulated = true;
        }
        return charm;
    }

    private getTicketCharmResults(result: ICharm): angular.IPromise<CharmResult> {
        return this._ticketService
            .getTicketCounts()
            .then(data => {
                const ticketData = this.getTicketData(data);
                result.go = () => this._$state.go(ticketData.linkTarget);
                let text: string;
                let values: Object;

                if (ticketData.numberOfTickets > 0) {
                    text = ticketData.text;
                    values = { numberOfTickets: ticketData.numberOfTickets };
                }
                result.isLoading = false;
                return this._$q.when({
                    text: text,
                    values: values,
                    isWarning: ticketData.isWarning,
                    isDanger: ticketData.isDanger
                });
            });
    }

    private getTicketData(ticketCounts: TicketCounts): CharmResult {
        const result: CharmResult = {
            text: '',
            linkTarget: 'ticketing.kanban',
            numberOfTickets: 0,
            isWarning: false,
            isDanger: false
        };
        if (ticketCounts.overdueTickets) {
            result.text = '_numberOfTickets_ tickets are overdue';
            result.numberOfTickets = ticketCounts.overdueTickets;
            result.isDanger = true;
        } else if (ticketCounts.overdueActionItems) {
            result.text = '_numberOfTickets_ action items are overdue';
            result.numberOfTickets = ticketCounts.overdueActionItems;
            result.isDanger = true;
            result.linkTarget = 'ticketing.actionItems';
        } else if (ticketCounts.rejectedTickets) {
            result.text = '_numberOfTickets_ tickets have been rejected and need to be closed';
            result.numberOfTickets = ticketCounts.rejectedTickets;
            result.isDanger = true;
        } else if (ticketCounts.nearlyDueTickets) {
            result.text = '_numberOfTickets_ tickets are nearly due';
            result.numberOfTickets = ticketCounts.nearlyDueTickets;
            result.isWarning = true;
        } else if (ticketCounts.nearlyDueActionItems) {
            result.text = '_numberOfTickets_ action items are nearly due';
            result.numberOfTickets = ticketCounts.nearlyDueActionItems;
            result.isWarning = true;
            result.linkTarget = 'ticketing.actionItems';
        } else if (ticketCounts.completedTickets) {
            result.text = '_numberOfTickets_ tickets are completed and need to be closed';
            result.numberOfTickets = ticketCounts.completedTickets;
            result.isWarning = true;
        } else if (ticketCounts.unassignedTickets) {
            result.text = '_numberOfTickets_ tickets need to be assigned';
            result.numberOfTickets = ticketCounts.unassignedTickets;
            result.isWarning = true;
        } else if (ticketCounts.assignedTickets) {
            result.text = '_numberOfTickets_ tickets are assigned to you';
            result.numberOfTickets = ticketCounts.assignedTickets;
        } else if (ticketCounts.assignedActionItems) {
            result.text = '_numberOfTickets_ action items are assigned to you';
            result.numberOfTickets = ticketCounts.assignedActionItems;
            result.linkTarget = 'ticketing.actionItems';
        }
        return result;
    }

    private createManualInputsCharm(): ICharm {
        const charm: ICharm = {
            icon: 'fa-keyboard-o',
            color: '#990',
            go: () => this._$state.go('manualInputs'),
            text: 'Manual inputs',
            isLoading: true,
            stateName: 'manualInputs',
            callback: () => this.getManualInputsCharmResults(charm),
            id: 'manualInputs',
            isPopulated: false,
            populate: (charm: ICharm, store: Store) => this.populateManualInputsCharm(charm, store),
            isVisible: false,
            values: {}
        };
        return charm;
    }

    populateManualInputsCharm(charm: ICharm, store: Store): ICharm {
        if (store.tags.isLoaded && store.connectionPoints.isLoaded && !charm.isPopulated) {
            charm.isPopulated = true;
            const { tags, connectionPoints } = store;
            const tagsList = _.values(tags.dictionary);
            const cpList = _.values(connectionPoints.dictionary);

            const manualInputs = cpList
                .filter(cp => cp.type === ConnectionPointType.Manual)
                .map(cp => cp.guid);
            const hasManualInputs = manualInputs && manualInputs.length
                && tagsList.filter(t => manualInputs.indexOf(t.acquisitionPointGuid) >= 0).length > 0;

            if (hasManualInputs) {
                const numberOfMissing = 0;
                const period = this.getLastSevenDays();
                charm.isVisible = hasManualInputs;
                charm.go = () => this._$state.go('manualInputs', period);
                charm.callback = () => this.getManualInputsCharmResults(charm);
                charm.values = { numberOfMissing };
            }
        }
        return charm;
    }

    private getManualInputsCharmResults(charm: ICharm): angular.IPromise<CharmResult> {
        return this._manualInputService
            .getLastManualInputsStatuses()
            .then(data => {
                let text: string;
                let values: Object;
                if (data.numberOfMissingData > 0) {
                    text = '_numberOfMissing_ missing manual inputs';
                    values = { numberOfMissing: data.numberOfMissingData };
                }
                charm.isLoading = false;
                return {
                    text,
                    values,
                    isWarning: data.status === 1,
                    isDanger: data.status === 2
                };
            });
    }

    private getLastSevenDays(): IStringDateRange2 {
        const currentApp = this._currentApplicationService.getApplicationConfiguration();
        const endMoment = moment().startOf('day').add(currentApp.beginDayTime, 'h');
        const period = DatesHelper.timeUnitToStartOf(currentApp.intervalTimeUnit);
        const beginMoment = endMoment.clone().add(-7 * currentApp.interval, period);

        return {
            beginDate: beginMoment.toISOString(),
            endDate: endMoment.toISOString()
        };
    }

    private createBulkInsertCharm(): ICharm {
        return {
            icon: 'fa-cubes',
            color: '#990',
            go: () => this._$state.go('bulkInsert'),
            text: 'Bulk insert',
            isLoading: false,
            values: {},
            stateName: 'bulkInsert',
            id: 'bulkInsert',
            isPopulated: false,
            populate: (charm: ICharm, store: Store) => this.populateBulkInsertCharm(charm, store),
            isVisible: false
        };
    }

    private populateBulkInsertCharm(charm: ICharm, store: Store): ICharm {
        if (store.tags.isLoaded && store.connectionPoints.isLoaded && !charm.isPopulated) {
            charm.isPopulated = true;
            const { tags, connectionPoints } = store;
            const tagsList = _.values(tags.dictionary);
            const cpList = _.values(connectionPoints.dictionary);
            const bulkCp = cpList
                .filter(cp => cp.type === ConnectionPointType.ManualFile || cp.bulkInsertEnabled)
                .map(cp => cp.guid);
            let hasBulkInsert = false;
            if (bulkCp && bulkCp.length > 0) {
                const tagsCp = tagsList.map(t => t.acquisitionPointGuid);
                hasBulkInsert = _.intersection(bulkCp, tagsCp).length > 0;
            }
            charm.isVisible = hasBulkInsert;
            charm.isLoading = false;
        }
        return charm;
    }

    private createDataFolderCharm(): ICharm {
        const charm: ICharm = {
            icon: 'fa-folder-open-o',
            color: '#990',
            go: () => this._$state.go('dataFolder', { files: null }),
            text: '_filesNumber_ Files to be processed',
            isLoading: false,
            stateName: 'dataFolder',
            callback: () => this.getDataFolderCharmResult(charm),
            id: 'dataFolder',
            isPopulated: false,
            populate: (charm: ICharm, store: Store) => this.populateDataFolderCharm(charm, store),
            isVisible: false,
            values: { filesNumber: 0 }
        };
        return charm;
    }

    private populateDataFolderCharm(charm: ICharm, store: Store): ICharm {
        if (store.connectionPoints.isLoaded && !charm.isPopulated) {
            charm.isPopulated = true;
            const { connectionPoints } = store;
            const cpList = _.values(connectionPoints.dictionary);
            const dataFolderCp = cpList
                .filter(cp => cp.type === ConnectionPointType.DataFolder)
                .map(cp => cp.guid);
            const hasDataFolder = dataFolderCp && dataFolderCp.length > 0;
            charm.isVisible = hasDataFolder;
        }
        return charm;
    }

    private getDataFolderCharmResult(charm: ICharm): angular.IPromise<CharmResult> {
        return this._dataFolderService
            .list()
            .then(dto => {
                charm.go = () => this._$state.go('dataFolder', { files: dto });
                charm.isLoading = false;
                return {
                    isDanger: false,
                    isWarning: dto.count > 0,
                    values: { filesNumber: dto.count }
                };
            });
    }

    private createApprovalCharm(): ICharm {
        return {
            icon: 'fa-thumbs-o-up',
            color: '#990',
            go: () => this._$state.go('approval'),
            text: 'Approval',
            isLoading: false,
            stateName: 'approval',
            id: 'approval',
            isPopulated: false,
            populate: (charm: ICharm, store: Store) => this.populateApprovalCharm(charm, store),
            isVisible: false,
            values: {}
        };
    }

    private populateApprovalCharm(charm: ICharm, store: Store): ICharm {
        if (store.application.isLoaded && !charm.isPopulated) {
            charm.isPopulated = true;
            charm.isVisible = store.application.value.validationEnabled;
            charm.isLoading = false;
        }
        return charm;
    }

    // TODO implement model integrity, over model charm ?
    private getModelIntegrity(): ICharm {
        return {
            icon: 'fa-sitemap',
            color: '#990',
            go: () => this._$state.go('modelIntegrity'),
            text: 'Model integrity',
            isLoading: false,
            stateName: 'modelIntegrity',
            id: 'modelIntegrity',
            isPopulated: false,
            populate: (charm: ICharm, store: Store) => this.populateModelIntegrityCharm(charm, store),
            isVisible: false,
            values: {}
        };
    }

    private populateModelIntegrityCharm(charm: ICharm, store: Store): ICharm {
        if (!charm.isPopulated) {
            charm.isPopulated = true;
            charm.isLoading = false;
        }
        return charm;
    }
}